import {
  isMeaningful,
  normalize,
  rowKey,
  stableTypeKey,
  type PipeElement,
  type ProjectVisibleType,
  type ProjectVisibleTypeIndex,
  type ResolvedParam,
} from './quickFillDataTypes';

export type { PipeElement, ProjectVisibleType, ProjectVisibleTypeIndex, ResolvedParam };
export { EMPTY_RESOLVED } from './quickFillDataTypes';
export type { ParameterBindingScope } from './quickFillDataTypes';

export interface ProjectElementIndexes {
  instanceParamIndex: Map<string, Map<string, ResolvedParam>>;
  instanceTargetIndex: Map<string, Map<string, string[]>>;
  modelElementIdIndex: Map<string, string[]>;
  projectVisibleTypeIndex: ProjectVisibleTypeIndex;
  projectVisibleTypeCount: number;
  projectVisibleTypeIdSamples: Array<string | undefined>;
  projectVisibleRowSamples: string[];
  elementsWithPayload: number;
}
export const getInstanceIndexKeys = (element: PipeElement): string[] => {
  const keys: string[] = [];
  if (element?.familyName) {
    keys.push(rowKey(element.familyName, element.typeName ?? null));
  }
  const typeKey = stableTypeKey(element?.typeId);
  if (typeKey) {
    keys.push(typeKey);
  }
  return Array.from(new Set(keys));
};
export const buildInstanceParamIndex = (elements: PipeElement[]): Map<string, Map<string, ResolvedParam>> => {
  const groups = new Map<string, PipeElement[]>();
  for (const element of elements) {
    if (!element) continue;
    for (const key of getInstanceIndexKeys(element)) {
      let group = groups.get(key);
      if (!group) { group = []; groups.set(key, group); }
      group.push(element);
    }
  }

  const index = new Map<string, Map<string, ResolvedParam>>();
  for (const [key, instances] of groups) {
    const paramMap = new Map<string, ResolvedParam>();

    const addCandidates = (requireMeaningful: boolean, requireWritable: boolean) => {
      for (const instance of instances) {
        const pvs = Array.isArray(instance.parameterValues) ? instance.parameterValues : [];
        for (const pv of pvs) {
          if (!pv || !pv.parameterName || paramMap.has(pv.parameterName)) continue;
          if (pv.isInstance === false) continue;
          if (requireMeaningful && !isMeaningful(pv.value)) continue;
          if (requireWritable && pv.isReadOnly === true) continue;

          paramMap.set(pv.parameterName, {
            value: isMeaningful(pv.value) ? String(pv.value) : '',
            isReadOnly: pv.isReadOnly === true,
            hasParameter: true,
            bindingScope: 'instance',
          });
        }
      }
    };

    addCandidates(true, true);
    addCandidates(true, false);
    addCandidates(false, true);
    addCandidates(false, false);
    index.set(key, paramMap);
  }
  return index;
};
export const buildInstanceTargetIndex = (elements: PipeElement[]): Map<string, Map<string, string[]>> => {
  const grouped = new Map<string, Map<string, Set<string>>>();

  for (const element of elements) {
    if (!element) continue;
    const elementId = typeof element.elementId === 'string' ? element.elementId.trim() : '';
    if (!elementId) continue;

    for (const key of getInstanceIndexKeys(element)) {
      let parameterTargets = grouped.get(key);
      if (!parameterTargets) {
        parameterTargets = new Map<string, Set<string>>();
        grouped.set(key, parameterTargets);
      }

      const pvs = Array.isArray(element.parameterValues) ? element.parameterValues : [];
      for (const pv of pvs) {
        if (!pv?.parameterName || pv.isReadOnly === true) continue;
        if (pv.isInstance === false) continue;
        let elementIds = parameterTargets.get(pv.parameterName);
        if (!elementIds) {
          elementIds = new Set<string>();
          parameterTargets.set(pv.parameterName, elementIds);
        }
        elementIds.add(elementId);
      }
    }
  }

  const index = new Map<string, Map<string, string[]>>();
  for (const [key, parameterTargets] of grouped) {
    index.set(
      key,
      new Map(Array.from(parameterTargets.entries()).map(([parameterName, elementIds]) => [
        parameterName,
        Array.from(elementIds),
      ]))
    );
  }

  return index;
};
export const buildModelElementIdIndex = (elements: PipeElement[]): Map<string, string[]> => {
  const grouped = new Map<string, Set<string>>();

  const add = (key: string | null | undefined, elementId: string) => {
    if (!key) {
      return;
    }

    let ids = grouped.get(key);
    if (!ids) {
      ids = new Set<string>();
      grouped.set(key, ids);
    }
    ids.add(elementId);
  };

  for (const element of elements) {
    if (!element) continue;
    const elementId = typeof element.elementId === 'string' ? element.elementId.trim() : '';
    if (!elementId) continue;

    for (const key of getInstanceIndexKeys(element)) {
      add(key, elementId);
    }

    if (element.familyName) {
      add(rowKey(element.familyName, null), elementId);
    }
  }

  return new Map(
    Array.from(grouped.entries()).map(([key, elementIds]) => [
      key,
      Array.from(elementIds),
    ])
  );
};
export const buildProjectVisibleTypeIndex = (elements: PipeElement[]): ProjectVisibleTypeIndex => {
  const grouped = new Map<string, Map<string, ProjectVisibleType>>();
  const byTypeId = new Map<string, ProjectVisibleType>();

  for (const element of elements) {
    const familyName = (element?.familyName || '').trim();
    if (!familyName) continue;
    const familyKey = normalize(familyName);
    const typeName = (element.typeName || '').trim();
    if (!typeName) continue;

    let types = grouped.get(familyKey);
    if (!types) {
      types = new Map<string, ProjectVisibleType>();
      grouped.set(familyKey, types);
    }

    const typeId = (element.typeId || '').trim();
    const key = typeId ? `id:${normalize(typeId)}` : `name:${normalize(typeName)}`;
    if (!types.has(key)) {
      const visibleType: ProjectVisibleType = {
        name: typeName,
        familyName,
        ...(typeId ? { typeId } : {}),
      };
      types.set(key, visibleType);
      const normalizedTypeId = stableTypeKey(typeId);
      if (normalizedTypeId && !byTypeId.has(normalizedTypeId)) {
        byTypeId.set(normalizedTypeId, visibleType);
      }
    }
  }

  return {
    byFamilyName: new Map(
      Array.from(grouped.entries()).map(([familyKey, types]) => [familyKey, Array.from(types.values())])
    ),
    byTypeId,
  };
};
let quickFillProjectElementIndexCache = new WeakMap<PipeElement[], ProjectElementIndexes>();
export const buildProjectElementIndexes = (elements: PipeElement[]): ProjectElementIndexes => {
  const instanceParamIndex = buildInstanceParamIndex(elements);
  const instanceTargetIndex = buildInstanceTargetIndex(elements);
  const modelElementIdIndex = buildModelElementIdIndex(elements);
  const projectVisibleTypeIndex = buildProjectVisibleTypeIndex(elements);
  const projectVisibleTypeCount = Array.from(projectVisibleTypeIndex.byFamilyName.values())
    .reduce((sum, types) => sum + types.length, 0);
  const projectVisibleTypeIdSamples = Array.from(projectVisibleTypeIndex.byTypeId.values())
    .map((type) => type.typeId)
    .filter(Boolean)
    .slice(0, 8);
  const projectVisibleRowSamples = Array.from(projectVisibleTypeIndex.byFamilyName.entries())
    .flatMap(([familyKey, types]) => types.map((type) => `${familyKey}:${type.name}${type.typeId ? `#${type.typeId}` : ''}`))
    .slice(0, 8);

  return {
    instanceParamIndex,
    instanceTargetIndex,
    modelElementIdIndex,
    projectVisibleTypeIndex,
    projectVisibleTypeCount,
    projectVisibleTypeIdSamples,
    projectVisibleRowSamples,
    elementsWithPayload: elements.filter((element) => Array.isArray(element?.parameterValues) && element.parameterValues.length > 0).length,
  };
};
export const getProjectElementIndexes = (
  elements: PipeElement[]
): ProjectElementIndexes & { cacheHit: boolean } => {
  const cached = quickFillProjectElementIndexCache.get(elements);
  if (cached) {
    return { ...cached, cacheHit: true };
  }

  const indexes = buildProjectElementIndexes(elements);
  quickFillProjectElementIndexCache.set(elements, indexes);
  return { ...indexes, cacheHit: false };
};

export const resetQuickFillProjectElementIndexCache = () => {
  quickFillProjectElementIndexCache = new WeakMap<PipeElement[], ProjectElementIndexes>();
};
